import { Suspense, lazy, useState } from 'react';

import { PageShell } from '@/components/layout/page-shell';
import { HeatmapGrid } from '@/components/feature/heatmap-grid';
import { KpiTile } from '@/components/feature/kpi-tile';
import { useAnalytics } from '@/hooks/use-analytics';
import type { AnalyticsRange } from '@/types/api';
import { cls, formatPercent } from '@/lib/format';

const StatsCharts = lazy(() => import('@/components/feature/stats-charts'));

const ranges: AnalyticsRange[] = ['7d', '30d', '90d'];

export default function StatsPage() {
  const [range, setRange] = useState<AnalyticsRange>('30d');
  const { data, isLoading, error } = useAnalytics(range);

  return (
    <PageShell title="Stats">
      <div className="sticky top-0 z-10 bg-[var(--color-bg)] px-4 pb-2 pt-1">
        <div className="flex gap-1 rounded-xl bg-[var(--color-secondary-bg)] p-1">
          {ranges.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={cls(
                'flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity',
                r === range
                  ? 'bg-[var(--color-bg)] text-[var(--color-fg)] shadow-sm'
                  : 'text-[var(--color-muted)]',
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-[var(--color-muted)]">Loading…</p>
      ) : error ? (
        <p className="px-4 py-6 text-sm text-[var(--color-destructive)]">
          Couldn't load analytics.
        </p>
      ) : data ? (
        <div className="flex flex-col gap-4 px-4 py-3">
          <section className="grid grid-cols-2 gap-3">
            <KpiTile
              label="Done"
              value={formatPercent(data.kpis.completion_rate)}
              hint="completion"
            />
            <KpiTile
              label="Week avg"
              value={data.kpis.avg_sessions_per_week.toFixed(1)}
              hint="sessions"
            />
            <KpiTile
              label="Active days"
              value={String(data.kpis.days_active)}
              hint={`in ${range}`}
            />
            <KpiTile label="Streak" value={String(data.kpis.current_streak)} hint="current" />
          </section>

          <section className="rounded-xl bg-[var(--color-secondary-bg)] px-4 py-3">
            <h3 className="mb-2 text-xs uppercase tracking-wide text-[var(--color-muted)]">
              Last 90 days
            </h3>
            <div className="overflow-x-auto">
              <HeatmapGrid cells={data.heatmap} />
            </div>
          </section>

          <Suspense
            fallback={
              <p className="py-6 text-center text-sm text-[var(--color-muted)]">Loading charts…</p>
            }
          >
            <StatsCharts mood={data.mood_series} categories={data.category_distribution} />
          </Suspense>
        </div>
      ) : null}
    </PageShell>
  );
}
